/**
 * t検定の計算関数
 * 計算式は docs/systems/keisuke/FORMULAS.md を参照
 */

import { KeisukeAdapterError } from '../keisukeAdapterI18n';

/**
 * t検定の結果
 * ノンパラ（Mann–Whitney / Wilcoxon）では df・SD・CI は持たない（z は t 欄に格納）。
 */
export interface TTestResult {
  /** t統計量。ノンパラでは近似 z */
  t: number;
  /** 自由度（パラメトリックのみ） */
  df?: number;
  pValue: number; // P値（両側検定）
  pValueOneSided: number; // P値（片側検定）
  /** 95%信頼区間（パラメトリックのみ） */
  confidenceInterval?: {
    lower: number;
    upper: number;
  };
  /** 群1の平均。ノンパラでは中央値 */
  mean1: number;
  /** 群2の平均。ノンパラでは中央値 */
  mean2: number;
  /** 群1の標準偏差（パラメトリックのみ） */
  stdDev1?: number;
  /** 群2の標準偏差（パラメトリックのみ） */
  stdDev2?: number;
  n1: number; // 群1のサンプル数
  n2: number; // 群2のサンプル数
  method: 'student' | 'welch' | 'mann-whitney' | 'wilcoxon'; // 使用した検定方法
}

export interface VarianceTestResult {
  f: number;
  df1: number;
  df2: number;
  pValue: number;
  variance1: number;
  variance2: number;
}

/**
 * 正則化不完全ベータ関数 I_x(a, b)
 * 数値計算は連分数展開で近似
 */
function regularizedIncompleteBeta(x: number, a: number, b: number): number {
  if (!Number.isFinite(x) || x <= 0) return 0;
  if (x >= 1) return 1;

  const logBeta = logGamma(a) + logGamma(b) - logGamma(a + b);
  const front = Math.exp(a * Math.log(x) + b * Math.log(1 - x) - logBeta);
  if (x < (a + 1) / (a + b + 2)) {
    return (front * betaContinuedFraction(a, b, x)) / a;
  }
  return 1 - (front * betaContinuedFraction(b, a, 1 - x)) / b;
}

/**
 * 連分数によるベータ関数近似
 */
function betaContinuedFraction(a: number, b: number, x: number): number {
  const maxIterations = 200;
  const epsilon = 3e-14;
  const tiny = 1e-30;

  let m2: number;
  let aa: number;
  let c = 1;
  let d = 1 - ((a + b) * x) / (a + 1);
  if (Math.abs(d) < tiny) d = tiny;
  d = 1 / d;
  let h = d;

  for (let m = 1; m <= maxIterations; m += 1) {
    m2 = 2 * m;

    aa = (m * (b - m) * x) / ((a + m2 - 1) * (a + m2));
    d = 1 + aa * d;
    if (Math.abs(d) < tiny) d = tiny;
    c = 1 + aa / c;
    if (Math.abs(c) < tiny) c = tiny;
    d = 1 / d;
    h *= d * c;

    aa = (-(a + m) * (a + b + m) * x) / ((a + m2) * (a + m2 + 1));
    d = 1 + aa * d;
    if (Math.abs(d) < tiny) d = tiny;
    c = 1 + aa / c;
    if (Math.abs(c) < tiny) c = tiny;
    d = 1 / d;
    const delta = d * c;
    h *= delta;

    if (Math.abs(delta - 1) < epsilon) break;
  }

  return h;
}

/**
 * log(Gamma) の近似（Lanczos）
 */
function logGamma(z: number): number {
  const coefficients = [
    0.99999999999980993, 676.5203681218851, -1259.1392167224028, 771.3234287776531,
    -176.6150291621406, 12.507343278686905, -0.13857109526572012, 0.000009984369578019572,
    0.00000015056327351493116,
  ];
  const g = 7;
  if (z < 0.5) {
    return Math.log(Math.PI) - Math.log(Math.sin(Math.PI * z)) - logGamma(1 - z);
  }
  let x = coefficients[0];
  const denom = z - 1;
  for (let i = 1; i < coefficients.length; i += 1) {
    x += coefficients[i] / (denom + i);
  }
  const t = denom + g + 0.5;
  return 0.5 * Math.log(2 * Math.PI) + (denom + 0.5) * Math.log(t) - t + Math.log(x);
}

/**
 * t分布の2側P値（Tukey HSD のプール t などからも利用）
 */
export function twoTailedPValue(t: number, df: number): number {
  return calculatePValue(t, df);
}

/**
 * t分布の2側P値
 */
function calculatePValue(t: number, df: number): number {
  if (!Number.isFinite(t) || !Number.isFinite(df) || df <= 0) return 1;
  const x = df / (df + t * t);
  const cdf =
    t >= 0
      ? 1 - 0.5 * regularizedIncompleteBeta(x, df / 2, 0.5)
      : 0.5 * regularizedIncompleteBeta(x, df / 2, 0.5);
  const p = 2 * Math.min(cdf, 1 - cdf);
  return Math.max(0, Math.min(1, p));
}

/**
 * t 分布の両側 α 臨界値: |t| がこの値のとき twoTailedPValue(|t|, df) = α。
 * 不完全ベータと同じ経路（calculatePValue）と整合。
 */
export function tDistributionTwoSidedCritical(df: number, alpha: number = 0.05): number {
  if (!Number.isFinite(df) || df <= 0 || !Number.isFinite(alpha) || alpha <= 0 || alpha >= 1) {
    return NaN;
  }
  const target = alpha;
  let lo = 0;
  let hi = 1;
  while (calculatePValue(hi, df) > target) {
    hi *= 2;
    if (hi > 1e12) {
      break;
    }
  }
  for (let iter = 0; iter < 200; iter += 1) {
    const mid = (lo + hi) * 0.5;
    if (calculatePValue(mid, df) > target) {
      lo = mid;
    } else {
      hi = mid;
    }
  }
  return (lo + hi) * 0.5;
}

/**
 * F(df1, df2) 分布の下側確率 P(F ≤ f)
 */
function fDistributionCdf(f: number, df1: number, df2: number): number {
  if (!Number.isFinite(f) || f <= 0 || df1 <= 0 || df2 <= 0) {
    return 0;
  }
  const x = (df1 * f) / (df1 * f + df2);
  return regularizedIncompleteBeta(x, df1 / 2, df2 / 2);
}

/**
 * 2 標本の分散比検定の両側 P 値（R の var.test(..., alternative="two.sided") と同じ）
 * F = v1/v2 ~ F(n1-1, n2-1) として、2×min(四隅の尾確率) をとる。
 */
function varianceRatioTwoSidedPValue(
  variance1: number,
  variance2: number,
  n1: number,
  n2: number
): number {
  const d1 = n1 - 1;
  const d2 = n2 - 1;
  if (d1 <= 0 || d2 <= 0) {
    return 1;
  }
  const F = variance1 / variance2;
  if (!Number.isFinite(F) || F <= 0) {
    return 1;
  }
  const p1 = fDistributionCdf(F, d1, d2);
  const p2 = 1 - p1;
  const invF = 1 / F;
  const p3 = fDistributionCdf(invF, d2, d1);
  const p4 = 1 - p3;
  const p = 2 * Math.min(p1, p2, p3, p4);
  return Math.max(0, Math.min(1, p));
}

/**
 * 等分散性のF検定（簡易版）
 */
export function fTestEqualVariance(group1: number[], group2: number[]): VarianceTestResult {
  if (group1.length < 2 || group2.length < 2) {
    throw new KeisukeAdapterError('fTestMinTwoPerGroup');
  }
  const n1 = group1.length;
  const n2 = group2.length;
  const mean1 = group1.reduce((sum, val) => sum + val, 0) / n1;
  const mean2 = group2.reduce((sum, val) => sum + val, 0) / n2;
  const variance1 = group1.reduce((sum, val) => sum + Math.pow(val - mean1, 2), 0) / (n1 - 1);
  const variance2 = group2.reduce((sum, val) => sum + Math.pow(val - mean2, 2), 0) / (n2 - 1);

  let f = variance1 / variance2;
  let df1 = n1 - 1;
  let df2 = n2 - 1;
  if (f < 1) {
    f = 1 / f;
    df1 = n2 - 1;
    df2 = n1 - 1;
  }

  return {
    f,
    df1,
    df2,
    pValue: varianceRatioTwoSidedPValue(variance1, variance2, n1, n2),
    variance1,
    variance2,
  };
}

/**
 * 対応のないt検定（Student's t-test）
 */
export function unpairedTTest(
  group1: number[],
  group2: number[],
  equalVariance: boolean = false
): TTestResult {
  if (group1.length === 0 || group2.length === 0) {
    throw new KeisukeAdapterError('emptyData');
  }
  if (group1.length < 2 || group2.length < 2) {
    throw new KeisukeAdapterError('minTwoPerGroup');
  }

  const n1 = group1.length;
  const n2 = group2.length;

  // 平均値
  const mean1 = group1.reduce((sum, val) => sum + val, 0) / n1;
  const mean2 = group2.reduce((sum, val) => sum + val, 0) / n2;

  // 標準偏差
  const variance1 = group1.reduce((sum, val) => sum + Math.pow(val - mean1, 2), 0) / (n1 - 1);
  const variance2 = group2.reduce((sum, val) => sum + Math.pow(val - mean2, 2), 0) / (n2 - 1);
  const stdDev1 = Math.sqrt(variance1);
  const stdDev2 = Math.sqrt(variance2);

  let t: number;
  let df: number;
  let method: 'student' | 'welch';
  /** 平均差 (mean1 - mean2) の標準誤差。t 統計量の分母と 95% CI の幅に同じものを使う */
  let seMeanDiff: number;

  if (equalVariance) {
    // Student's t-test（等分散を仮定）
    const pooledVariance = ((n1 - 1) * variance1 + (n2 - 1) * variance2) / (n1 + n2 - 2);
    seMeanDiff = Math.sqrt(pooledVariance * (1 / n1 + 1 / n2));
    t = (mean1 - mean2) / seMeanDiff;
    df = n1 + n2 - 2;
    method = 'student';
  } else {
    // Welch's t-test（等分散を仮定しない）
    const se1 = Math.sqrt(variance1 / n1);
    const se2 = Math.sqrt(variance2 / n2);
    seMeanDiff = Math.sqrt(se1 * se1 + se2 * se2);
    t = (mean1 - mean2) / seMeanDiff;

    // Welch-Satterthwaite equation
    const dfNumerator = Math.pow(se1 * se1 + se2 * se2, 2);
    const dfDenominator = Math.pow(se1 * se1, 2) / (n1 - 1) + Math.pow(se2 * se2, 2) / (n2 - 1);
    df = dfNumerator / dfDenominator;
    method = 'welch';
  }

  // P値の計算
  const pValue = calculatePValue(t, df);
  const pValueOneSided = pValue / 2;

  // 95%信頼区間（mean1 - mean2）。検定と同じ SE・同じ df の臨界値を使用
  const tCritical = tDistributionTwoSidedCritical(df, 0.05);
  const margin = tCritical * seMeanDiff;
  const confidenceInterval = {
    lower: mean1 - mean2 - margin,
    upper: mean1 - mean2 + margin,
  };

  return {
    t,
    df,
    pValue,
    pValueOneSided,
    confidenceInterval,
    mean1,
    mean2,
    stdDev1,
    stdDev2,
    n1,
    n2,
    method,
  };
}

/**
 * 対応のあるt検定（Paired t-test）
 */
export function pairedTTest(before: number[], after: number[]): TTestResult {
  if (before.length !== after.length) {
    throw new KeisukeAdapterError('pairedCountMismatch');
  }

  if (before.length === 0) {
    throw new KeisukeAdapterError('emptyData');
  }
  if (before.length < 2) {
    throw new KeisukeAdapterError('minTwoDataPoints');
  }

  const n = before.length;

  // 差を計算
  const differences = before.map((b, i) => after[i] - b);

  // 差の平均値
  const meanDiff = differences.reduce((sum, val) => sum + val, 0) / n;

  // 差の標準偏差
  const varianceDiff =
    differences.reduce((sum, val) => sum + Math.pow(val - meanDiff, 2), 0) / (n - 1);
  const stdDevDiff = Math.sqrt(varianceDiff);

  // 標準誤差
  const seDiff = stdDevDiff / Math.sqrt(n);

  const df = n - 1;

  // 差分の分散が0のときはt検定が定義できない
  const isDegenerate = seDiff === 0 || !Number.isFinite(seDiff);
  const t = isDegenerate ? NaN : meanDiff / seDiff;

  // P値の計算
  const pValue = isDegenerate ? NaN : calculatePValue(t, df);
  const pValueOneSided = isDegenerate ? NaN : pValue / 2;

  // 95%信頼区間
  const tCritical = tDistributionTwoSidedCritical(df, 0.05);
  const margin = isDegenerate ? 0 : tCritical * seDiff;
  const confidenceInterval = {
    lower: meanDiff - margin,
    upper: meanDiff + margin,
  };

  // BeforeとAfterの統計量
  const mean1 = before.reduce((sum, val) => sum + val, 0) / n;
  const mean2 = after.reduce((sum, val) => sum + val, 0) / n;
  const variance1 = before.reduce((sum, val) => sum + Math.pow(val - mean1, 2), 0) / (n - 1);
  const variance2 = after.reduce((sum, val) => sum + Math.pow(val - mean2, 2), 0) / (n - 1);
  const stdDev1 = Math.sqrt(variance1);
  const stdDev2 = Math.sqrt(variance2);

  return {
    t,
    df,
    pValue,
    pValueOneSided,
    confidenceInterval,
    mean1,
    mean2,
    stdDev1,
    stdDev2,
    n1: n,
    n2: n,
    method: 'student', // 対応のあるt検定は常にStudent's t-test
  };
}
